import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { InteractiveElement } from '~/stores/interactiveElements.store';
import { dispatchButtonAction } from '~/services/elements/actionDispatcher';

interface Props {
  element: InteractiveElement;
}

const ButtonWrapper = styled.div<{
  $x: number;
  $y: number;
  $w: number;
  $h: number;
  $bg: string;
  $textColor: string;
  $opacity: number;
}>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $w }) => $w}%;
  height: ${({ $h }) => $h}%;
  background: ${({ $bg, $opacity }) => ($opacity > 0 ? $bg : 'transparent')};
  color: ${({ $textColor }) => $textColor};
  opacity: ${({ $opacity }) => ($opacity > 0 ? $opacity : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border: none;
  outline: none;
  transition: transform 100ms ease;

  &:active {
    transform: scale(0.97);
  }
`;

const InteractiveButton: React.FC<Props> = ({ element }) => {
  const [isActive, setIsActive] = useState(element.inSeconds <= 0);
  const lastTapRef = useRef(0);

  useEffect(() => {
    const timers: number[] = [];
    if (element.inSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(true), element.inSeconds * 1000),
      );
    }
    if (element.outSeconds !== null && element.outSeconds > 0) {
      timers.push(
        window.setTimeout(() => setIsActive(false), element.outSeconds * 1000),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [element.inSeconds, element.outSeconds]);

  const handleClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isActive) return;
      e.stopPropagation();
      e.preventDefault();
      const now = Date.now();
      // Debounce 300ms cobre o duplo evento touchstart+click sintético do
      // Samsung One UI 7 / Android 16 (preventDefault no touchstart às vezes
      // não impede o click sintético — vide fix análogo no KioskGuard v1.0.130).
      if (now - lastTapRef.current < 300) return;
      lastTapRef.current = now;
      dispatchButtonAction(element);
    },
    [element, isActive],
  );

  return (
    <ButtonWrapper
      $x={element.xPct}
      $y={element.yPct}
      $w={element.wPct}
      $h={element.hPct}
      $bg={element.bgColor}
      $textColor={element.textColor}
      $opacity={element.opacity}
      onTouchStart={handleClick}
      onClick={handleClick}
      style={{
        opacity: isActive ? undefined : 0,
        pointerEvents: isActive ? 'auto' : 'none',
      }}
    >
      {element.opacity > 0 && element.label}
    </ButtonWrapper>
  );
};

export default InteractiveButton;
